import { Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { DateRangeReportFilter, IdReportFilter, RecencyReportFilter, ReportTemplateTypes } from "../../api/dtos";
import { DetailsContent, DetailsHeader, DetailsPanel, GridData, GridLabel, GridRow } from "../../components/DetailsPanel";
import SetLinks from "../../components/layout/SetLinks";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import TitleBox from "../../components/TitleBox";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import ReportTemplatePreviewContext from "../../contexts/ReportTemplatePreviewContext";
import useReportTemplateById from "../../hooks/data/useReportTemplateById";
import useReportTemplatePreviewByIdFilters from "../../hooks/data/useReportTemplatePreviewByIdFilters";
import { useAssetsColumns, useColumnGroupingModel, useCommissionReportColumns, useContractCancellationSummaryColumns, useContractsOverdueColumns, useGrossRealisationColumns, useLotPricingComparisonColumns, useLotSummaryColumns, useSalesAdviceToContractColumns, useSalesRateColumns, useSalesSummaryColumns, useStageLotCountsColumns, useUpcomingSettlementsColumns } from "./ReportColumnDefinitions";
import ReportTemplatePreviewFilters from "./ReportTemplatePreviewFilters";

// const useTemplateFieldColumns = (fields?: ReportCustomTemplateField[]) => {
//     const {data: fieldsWithNames} = useReportCustomTemplateFields();

//     const fieldColumns = useMemo(() => {

//         const getFieldSpecifier = (field: ReportCustomTemplateFieldWithName) => {
//             return field.field.fieldType == ReportCustomTemplateFieldTypes.LotProperty 
//                 ? field.name 
//                 : field.field.fieldType
//                 ? ReportCustomTemplateFieldTypes[field.field.fieldType]
//                 : "";
//         }

//         const getValueGetter = (fieldType?: ReportCustomTemplateFieldTypes, value?: any) => {
//             switch(fieldType) {
//                 case ReportCustomTemplateFieldTypes.ContractCommission:
//                 case ReportCustomTemplateFieldTypes.ContractDeposit:
//                 case ReportCustomTemplateFieldTypes.ContractLegalFees:
//                 case ReportCustomTemplateFieldTypes.ContractRateAdjustments:
//                 case ReportCustomTemplateFieldTypes.ContractRebate:
//                 case ReportCustomTemplateFieldTypes.ContractGst:
//                 case ReportCustomTemplateFieldTypes.ContractPrice:
//                 case ReportCustomTemplateFieldTypes.ContractWaterAdjustments:
//                 case ReportCustomTemplateFieldTypes.LotAdvertisedPrice:
//                 case ReportCustomTemplateFieldTypes.LotApprovedRebate:
//                 case ReportCustomTemplateFieldTypes.LotLandValue:
//                 case ReportCustomTemplateFieldTypes.ProjectCostPerLot:
//                 case ReportCustomTemplateFieldTypes.ProjectDefaultCommission:
//                 case ReportCustomTemplateFieldTypes.ProjectPercentShare:
//                 case ReportCustomTemplateFieldTypes.ProjectValuation:
//                 case ReportCustomTemplateFieldTypes.StageArea:
//                 case ReportCustomTemplateFieldTypes.StageBudget:
//                 case ReportCustomTemplateFieldTypes.StageCostPerLot:
//                     const val = Math.round(Number(value)*100)/100;
//                     return Number.isNaN(val) ? undefined : val;

//                 case ReportCustomTemplateFieldTypes.ContractActualSettleDate:
//                 case ReportCustomTemplateFieldTypes.ContractDateBackOnMarket:
//                 case ReportCustomTemplateFieldTypes.ContractExchangeDate:
//                 case ReportCustomTemplateFieldTypes.ContractExpectedSettleDate:
//                 case ReportCustomTemplateFieldTypes.ContractIssueDate:
//                 case ReportCustomTemplateFieldTypes.LotDateOnMarket:
//                 case ReportCustomTemplateFieldTypes.LotLandValueDate:
//                 case ReportCustomTemplateFieldTypes.ProjectValuationDate:
//                 case ReportCustomTemplateFieldTypes.StageRegistrationDate:
//                     return value ? DateTime.fromFormat(value, "dd/MM/yyyy") : value;
//                 default:
//                     return value;
//             }
//         }

//         const getValueFormatter = (fieldType?: ReportCustomTemplateFieldTypes, value?: any) => {
//             switch(fieldType) {
//                 case ReportCustomTemplateFieldTypes.ContractCommission:
//                 case ReportCustomTemplateFieldTypes.ContractDeposit:
//                 case ReportCustomTemplateFieldTypes.ContractLegalFees:
//                 case ReportCustomTemplateFieldTypes.ContractRateAdjustments:
//                 case ReportCustomTemplateFieldTypes.ContractRebate:
//                 case ReportCustomTemplateFieldTypes.ContractGst:
//                 case ReportCustomTemplateFieldTypes.ContractPrice:
//                 case ReportCustomTemplateFieldTypes.ContractWaterAdjustments:
//                 case ReportCustomTemplateFieldTypes.LotAdvertisedPrice:
//                 case ReportCustomTemplateFieldTypes.LotApprovedRebate:
//                 case ReportCustomTemplateFieldTypes.LotLandValue:
//                 case ReportCustomTemplateFieldTypes.ProjectCostPerLot:
//                 case ReportCustomTemplateFieldTypes.ProjectDefaultCommission:
//                 case ReportCustomTemplateFieldTypes.ProjectPercentShare:
//                 case ReportCustomTemplateFieldTypes.ProjectValuation:
//                 case ReportCustomTemplateFieldTypes.StageArea:
//                 case ReportCustomTemplateFieldTypes.StageBudget:
//                 case ReportCustomTemplateFieldTypes.StageCostPerLot:
//                     const val = Math.round(Number(value)*100)/100;
//                     return Number.isNaN(val) ? undefined : val;
//                 default:
//                     return value;
//             }
//         }

//         const columns: GridColDef[] = fieldsWithNames?.filter(fieldWithName => 
//             (fields?.filter(field => 
//                 field.fieldType == fieldWithName.field.fieldType &&
//                 field.fieldReferenceId == fieldWithName.field.fieldReferenceId
//             ).length ?? -1) > 0
//         ).map(field => {
//             return {
//                 headerName: field.name,
//                 field: getFieldSpecifier(field) ?? '',
//                 valueGetter: (_, row) => getValueGetter(field.field.fieldType, row[getFieldSpecifier(field)]),
//                 valueFormatter: (_, row) => getValueFormatter(field.field.fieldType, row[getFieldSpecifier(field)]),
//                 minWidth: 100,
//                 flex: 1
//             };
//         }) ?? [];

//         return columns;
//     }, [fieldsWithNames, fields]);

//     return fieldColumns;
// }

const ReportTemplatePreview: FC = () => {
    const params = useParams();
    const templateId = Number(params.templateId);

    const {data: template} = useReportTemplateById(templateId);
    const [idFilters, setIdFilters] = useState<IdReportFilter[]>([]);
    const [dateRangeFilters, setDateRangeFilters] = useState<DateRangeReportFilter[]>([]);
    const [recencyFilters, setRecencyFilters] = useState<RecencyReportFilter[]>([]);

    const contextData = useReportTemplatePreviewByIdFilters(template?.reportTemplate?.id, idFilters, dateRangeFilters, recencyFilters);
    const {data: data} = contextData;

    useEffect(() => {
        setIdFilters([]);
        setDateRangeFilters([]);
        setRecencyFilters([]);
    }, [template?.reportTemplate?.templateType])

    const salesSummaryColumns = useSalesSummaryColumns(data?.rows ?? []);
    const lotSummaryColumns = useLotSummaryColumns();
    const contractsOverdueColumns = useContractsOverdueColumns();
    const commissionReportColumns = useCommissionReportColumns();
    const salesAdviceToContractColumns = useSalesAdviceToContractColumns();
    const grossRealisationColumns = useGrossRealisationColumns(data?.rows ?? []);
    const salesRateColumns = useSalesRateColumns(data?.rows ?? []);
    const stageLotCountsColumns = useStageLotCountsColumns();
    const upcomingSettlementsColumns = useUpcomingSettlementsColumns();
    const lotPricingComparisonColumns = useLotPricingComparisonColumns();
    const assetsColumns = useAssetsColumns();
    const contractCancellationSummaryColumns = useContractCancellationSummaryColumns();

    const columns = useMemo(() => {
        switch(template?.reportTemplate?.templateType) {
            case ReportTemplateTypes.SalesSummary:
                return salesSummaryColumns;
            case ReportTemplateTypes.ContractsOverdue:
                return contractsOverdueColumns;
            case ReportTemplateTypes.LotSummary:
                return lotSummaryColumns;
            case ReportTemplateTypes.CommissionReport:
                return commissionReportColumns;
            case ReportTemplateTypes.SalesAdviceToContract:
                return salesAdviceToContractColumns;
            case ReportTemplateTypes.GrossRealisationReport:
                return grossRealisationColumns;
            case ReportTemplateTypes.SalesRateReport:
                return salesRateColumns;
            case ReportTemplateTypes.StageLotCounts:
                return stageLotCountsColumns;
            case ReportTemplateTypes.UpcomingSettlements:
                return upcomingSettlementsColumns;
            case ReportTemplateTypes.Assets:
                return assetsColumns;
            case ReportTemplateTypes.LotPricingComparison:
                return lotPricingComparisonColumns;
            case ReportTemplateTypes.ContractCancellationSummary:
                return contractCancellationSummaryColumns;
            default:
                return [];
        }
    }, [
        data, 
        template, 
        salesSummaryColumns, 
        contractsOverdueColumns,
        lotSummaryColumns, 
        commissionReportColumns, 
        salesAdviceToContractColumns, 
        grossRealisationColumns, 
        salesRateColumns, 
        stageLotCountsColumns, 
        upcomingSettlementsColumns,
        assetsColumns,
        lotPricingComparisonColumns,
        contractCancellationSummaryColumns
    ])

    const columnGroupingModel = useColumnGroupingModel(template?.reportTemplate?.templateType);

    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `${template?.reportTemplate?.name ?? "Unknown Report Template"} - Preview`,
        exportTitle: `${template?.reportTemplate?.name ?? "Unknown Report Template"} - Preview`,
        includeDateTime: true
    }), []);

    return (
        <ReportTemplatePreviewContext.Provider value={contextData}>
            <SetLinks
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <Outlet />
            <TitleBox>
                <Typography variant="h2">
                    {template?.reportTemplate?.name}
                </Typography>
            </TitleBox>
            {
                <ReportTemplatePreviewFilters
                    templateType={template?.reportTemplate?.templateType}
                    idFilters={idFilters}
                    setIdFilters={setIdFilters}
                    dateRangeFilters={dateRangeFilters}
                    setDateRangeFilters={setDateRangeFilters}
                    recencyFilters={recencyFilters}
                    setRecencyFilters={setRecencyFilters}
                />
            }
            {
                data?.summaries && 
                data.summaries.map((summary, index) => (
                    <DetailsPanel key={index}>
                        <DetailsHeader>Summary</DetailsHeader>
                        <DetailsContent>
                            {Object.keys(summary).map((key, index) => (
                                <GridRow key={index}>
                                    <GridLabel>{key.replace(/([A-Z])/g, ' $1').trim()}</GridLabel>
                                    <GridData>{summary[key]}</GridData>
                                </GridRow>
                            ))}
                        </DetailsContent>
                    </DetailsPanel>
                ))
                
            }
            <CollapsibleTablePremium
                sx={{
                    maxHeight: 800
                }}
                title={"Entries"} 
                loading={false} 
                columns={columns}
                density="compact"
                rows={data?.rows?.map((row, index) => {return {id: row.id ?? index, ...row}})}
                disableColumnResize={false}
                disableColumnFilter={false}
                disableColumnMenu={false}
                columnGroupingModel={columnGroupingModel}
                {...customToolbarProps}
            />
        </ReportTemplatePreviewContext.Provider>
    );
}

export default ReportTemplatePreview;